/**
 * Holds the identity session for this tab (ported from dwlf-metals-app's src/auth/sessionManager.ts).
 * The access token lives in memory only; the refresh token is kept in localStorage so a reload, or another tab, can carry on the session (identity's
 * charter records an httpOnly cookie on id.dwlf.co.uk as the later hardening).
 *
 * Refresh follows identity's protocol. One refresh runs at a time: callers in this tab share it,
 * and tabs take turns through a Web Lock, each rotating whatever token is stored when its turn
 * comes. A `refresh_in_progress` answer means this token was rotated a moment ago by someone else:
 * a tab in a browser without Web Locks, or a thief. Outside the lock (so no other tab waits behind
 * it), wait up to identity's grace for a newer token to be stored and carry on with it; if none
 * comes, revoke the family and sign out. A rotation whose response was lost on the way to this tab
 * also lands here and cannot be recovered (the new token went nowhere): identity's charter accepts
 * that it reads as reuse. An ended session (401) signs this tab out. A busy or unreachable identity
 * is not a sign-out.
 */
import { IdentityError, RefreshInProgressError } from './errors';
export const REFRESH_KEY = 'dwlf-retail.refresh';
/** Refresh this long before the access token expires, so no request carries a token about to lapse. */
const EARLY_MS = 60_000;
/** How long to wait for another tab's rotation to land: identity's grace for a concurrent refresh. */
export const RACE_WAIT_MS = 10_000;
const RACE_POLL_MS = 250;
/** localStorage, falling back to nothing when storage is blocked: the session then lasts the page. */
export const browserStore = {
  get: () => {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      localStorage.setItem(REFRESH_KEY, token);
    } catch {
      // Storage blocked: nothing survives the page.
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      // Storage blocked: nothing was stored.
    }
  },
};
export const browserLock = (work) => typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(REFRESH_KEY, work) : work();
/** Waits for a token other than `token` to be stored; null if none arrives within identity's grace. */
async function newerThan(store, sleep, token) {
  for (let waited = 0; waited <= RACE_WAIT_MS; waited += RACE_POLL_MS) {
    const stored = store.get();
    if (stored !== null && stored !== token) {
      return stored;
    }
    await sleep(RACE_POLL_MS);
  }
  return null;
}
const subscribe = (set) => (listener) => {
  set.add(listener);
  return () => set.delete(listener);
};
const sameSession = (a, b) => a.user.userId === b.user.userId && a.account.accountId === b.account.accountId;
/** One refresh by identity's protocol: rotate under the lock, and handle a race outside it (see the module doc). */
function rotation({ identity, store, lock, sleep, adopt, end }) {
  /** One rotation of `token`; `raced` when identity says it was rotated a moment ago. */
  async function rotate(token) {
    try {
      return adopt(await identity.refresh(token));
    } catch (error) {
      if (error instanceof RefreshInProgressError) {
        return 'raced';
      }
      if (error instanceof IdentityError && error.status === 401) {
        return end();
      }
      throw error;
    }
  }
  /** Revokes the whole family (identity's protocol: a thief may have won the race), then signs out. */
  async function revoke(token) {
    // Unreachable identity: the local session is gone anyway, and the family's reuse detection
    // still catches whoever holds the other token.
    await identity.signOut(token).catch(() => undefined);
    return end();
  }
  /** Under the lock, rotates whatever is stored at that moment: a tab queued behind another gets its new token. */
  const rotateStored = () => lock(async () => {
    const token = store.get();
    return { token, result: token === null ? end() : await rotate(token) };
  });
  async function settle() {
    const first = await rotateStored();
    if (first.result !== 'raced') {
      return first.result;
    }
    const raced = first.token;
    if ((await newerThan(store, sleep, raced)) === null) {
      return revoke(raced);
    }
    const second = await rotateStored();
    return second.result === 'raced' ? revoke(second.token) : second.result;
  }
  return settle;
}
export function createSessionManager(identity, options = {}) {
  const store = options.store ?? browserStore;
  const lock = options.lock ?? browserLock;
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  let current = null;
  let inflight = null;
  const ended = new Set();
  const switched = new Set();
  function adopt(session) {
    store.set(session.refreshToken);
    current = { accessToken: session.accessToken, expiresAt: now() + session.expiresIn * 1000, account: session.account, user: session.user };
    return current;
  }
  function end() {
    const wasSignedIn = current !== null || store.get() !== null;
    current = null;
    store.clear();
    if (wasSignedIn) {
      ended.forEach((l) => l());
    }
    return null;
  }
  const settle = rotation({ identity, store, lock, sleep, adopt, end });
  function refresh() {
    inflight ??= (async () => {
      const before = current;
      const after = await settle();
      if (before !== null && after !== null && !sameSession(before, after)) {
        switched.forEach((l) => l(after));
      }
      return after;
    })().finally(() => {
      inflight = null;
    });
    return inflight;
  }
  return {
    adopt,
    hasStoredSession: () => store.get() !== null,
    async accessToken() {
      if (current !== null && current.expiresAt - now() > EARLY_MS) {
        return current.accessToken;
      }
      return (await refresh())?.accessToken ?? null;
    },
    refresh,
    async signOut() {
      const token = store.get();
      end();
      if (token !== null) {
        // Unreachable identity: signed out here regardless; the refresh token expires on its own.
        await identity.signOut(token).catch(() => undefined);
      }
    },
    onEnded: subscribe(ended),
    onSwitched: subscribe(switched),
  };
}
